import {
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  signal,
  untracked,
} from "@angular/core";
import { MultiDateComponent } from "../../../../shared/modules/multi-date/multi-date.component";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SharedModule } from "../../../../shared/shared.module";
import { ArticleFacadeService } from "../../services/article/article-facade.service";
import { MouvementFacadeService } from "../../../lot/services/mouvement-store/mouvement-facade.service";
import { ArticlesInfosFacadeService } from "../../services/article-infos/article-infos-facade.service";
import { ArticleInfosHelperService } from "../../services/article-infos/article-infos-helper.service";
import { Router } from "@angular/router";
import { DashboardContainerComponent } from "../../../dashboard/containers/dashboard/dashboard-container.component";
import { TypeDashboard } from "../../../dashboard/enums/dashboard-type.enum";
import { SearchUtils } from "../../../../shared/utils/search.utils";
import { InputSearchDialogComponent } from "../../../../shared/modules/dialog/input-search-dialog/input-search-dialog.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DashboardFacadeService } from "../../../dashboard/services/dashboard-facade.service";
import { WidgetType } from "../../../dashboard/models/dashboard.model";
import { ActivityInfosService } from "../../../activity_infos/services/activity_infos.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { HeaderDialogComponent } from "../../../../shared/modules/dialog/header-dialog/header-dialog.component";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  NativeDateAdapter,
} from "@angular/material/core";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

export const FR_NATIVE_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    // Pour NativeDateAdapter, ce sont des options Intl
    dateInput: { year: "numeric", month: "2-digit", day: "2-digit" },
    monthYearLabel: { year: "numeric", month: "short" },
    dateA11yLabel: { year: "numeric", month: "long", day: "numeric" },
    monthYearA11yLabel: { year: "numeric", month: "long" },
  },
};

export class FrNativeDateAdapter extends NativeDateAdapter {
  override parse(value: unknown): Date | null {
    if (typeof value === "string") {
      const v = value.trim();
      if (!v) return null;

      // 14/01/2026 ou 14-01-2026 ou 14.01.2026
      const m = v.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
      if (m) {
        const day = parseNumber(m[1]);
        const month = parseNumber(m[2]);
        const year = parseNumber(m[3]);

        const d = new Date(year, month - 1, day);

        // Validation stricte
        if (
          d.getFullYear() === year &&
          d.getMonth() === month - 1 &&
          d.getDate() === day
        ) {
          return d;
        }
        return null;
      }
    }
    return super.parse(value);
  }
}

@Component({
  selector: "vl-stats-article",
  standalone: true,
  imports: [
    MatFormField,
    MatIcon,
    MatLabel,
    ChipsToggleComponent,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    MultiDateComponent,
    DashboardContainerComponent,
    InputSearchDialogComponent,
    ChipsComponent,
    HeaderDialogComponent,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "fr-FR" },
    { provide: MAT_DATE_FORMATS, useValue: FR_NATIVE_DATE_FORMATS },
    {
      provide: DateAdapter,
      useClass: FrNativeDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
  templateUrl: "./stats-article.component.html",
  styleUrl: "./stats-article.component.scss",
})
export class StatsArticleComponent {
  articleFacade = inject(ArticleFacadeService);
  mouvementFacade = inject(MouvementFacadeService);
  aiFacade = inject(ArticlesInfosFacadeService);
  router = inject(Router);
  dashboardFacade = inject(DashboardFacadeService);
  activityInfosService = inject(ActivityInfosService);
  activityInfos = toSignal(this.activityInfosService.currentActivityInfos$);

  articleIdInput = model<string>();
  articleId = model<string>();

  searchValue = signal<string>(null);

  allAis = this.aiFacade.getAllEntities();

  allAisFiltered = computed(() => {
    const list = this.allAis() ?? [];
    const q = (this.searchValue() ?? "").trim();

    if (!q) {
      // pas de recherche → on retourne simplement les 15 premiers
      return list.slice(0, 15);
    }

    const results = list
      .map((ai) => {
        const sDen = SearchUtils.isIncludedScore(q, ai?.denomination ?? "");
        const sCode = SearchUtils.isIncludedScore(q, ai?.code ?? "");
        // On garde le meilleur des deux, avec un petit bonus si le code matche
        const score = Math.max(sDen, sCode) + (sCode > 0 ? 0.6 : 0);
        return { ai, score };
      })
      .filter((r) => r.score > 0) // élimine les non-pertinents
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score; // tri par score décroissant
        // tie-breakers stables (facultatif)
        const aDen = (a.ai?.denomination ?? "").toString();
        const bDen = (b.ai?.denomination ?? "").toString();
        return aDen.localeCompare(bDen, "fr", { sensitivity: "base" });
      })
      .slice(0, 15)
      .map((r) => r.ai);

    return results;
  });

  startSearch = signal(false);

  // ————————————————————————————————————————————————————————
  // MOCK: informations article
  close = output();

  articleInfos = computed(() => {
    if (this.articleId()) {
      return this.aiFacade.getByArticleId(this.articleId())();
    }
  });
  uniteDenomination = computed(() => {
    if (this.articleInfos()) {
      return ArticleInfosHelperService.uniteDenomination(this.articleInfos());
    }
  });
  stats = this.articleFacade.getState().stats;

  period = model<string>("Exercice");
  periods = signal([
    {
      name: "1M",
      value: "1M",
    },
    {
      name: "3M",
      value: "3M",
    },
    {
      name: "12M",
      value: "12M",
    },
    {
      name: "Exercice en cours",
      value: "Exercice",
    },
    {
      name: "Exercice passé",
      value: "Exercice passé",
    },
    {
      name: "Tout",
      value: "ALL",
    },
  ]);

  current = model<string>("Stock actuel");

  // Dates (mockées) + filtres rapides
  startDate = signal<Date | null>(
    new Date(new Date().setDate(new Date().getDate() - 30)),
  );
  endDate = signal<Date | null>(new Date());

  dateRange = computed(() =>
    this.startDate() && this.endDate()
      ? { start: this.startDate(), end: this.endDate() }
      : null,
  );

  onDateRangeChange(range: { start: Date; end: Date }) {
    if (range?.start) this.startDate.set(range.start);
    if (range?.end) this.endDate.set(range.end);
  }

  search = signal<string | null>(null);

  constructor() {
    this.articleFacade.initState();
    effect(
      () => {
        if (this.articleIdInput()) {
          this.articleId.set(this.articleIdInput());
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const searchValue = this.searchValue();
        if (!searchValue) {
          this.startSearch.set(false);
        } else {
          this.startSearch.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const activiteInfo = this.activityInfos();

        if (this.period()) {
          const which = this.period();
          const now = new Date();
          if (which === "1M")
            this.startDate.set(
              new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
            );
          if (which === "3M")
            this.startDate.set(
              new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
            );
          if (which === "12M")
            this.startDate.set(
              new Date(now.getFullYear(), now.getMonth() - 12, now.getDate()),
            );
          if (which === "Exercice") {
            const year = now.getFullYear();

            if (activiteInfo.dateDebutExercice) {
              let d = this.ddmmToDate(activiteInfo.dateDebutExercice, year);

              // Si le JJ/MM est "après" aujourd'hui, c'est forcément le début d'exercice de l'année précédente
              if (d.getTime() > now.getTime()) {
                d = this.ddmmToDate(activiteInfo.dateDebutExercice, year - 1);
              }

              this.startDate.set(d);
            } else {
              this.startDate.set(new Date(year, 0, 1));
            }
          }

          if (which === "Exercice passé") {
            if (activiteInfo.dateDebutExercice) {
              const y = new Date().getFullYear();
              const d = this.ddmmToDate(activiteInfo.dateDebutExercice, y - 1);
              const dFin = this.ddmmToDate(activiteInfo.dateDebutExercice, y);
              this.startDate.set(d);
              this.endDate.set(dFin);
            } else {
              this.startDate.set(new Date(now.getFullYear() - 1, 0, 1));
              this.endDate.set(new Date(now.getFullYear() - 1, 11, 31));
            }
            return;
          }

          if (which === "ALL")
            this.startDate.set(new Date(2020, now.getMonth(), now.getDate()));

          now.setDate(now.getDate() + 1);
          this.endDate.set(now);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      (onCleanup) => {
        const articleId = this.articleId();
        const start = this.startDate();
        const end = this.endDate();
        const search = this.search();

        if (!start || !end) return;

        const timer = setTimeout(() => {
          this.articleFacade.stats({
            articleId,
            dateDebut: start,
            dateFin: end,
            search,
          });
        }, 300);

        // Annule le timer si l’effect est relancé avant les 300 ms
        onCleanup(() => clearTimeout(timer));
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const stats = this.stats();

        if (!stats) {
          return;
        }

        let widgetToSqueeze = [];

        untracked(() => {
          if (!stats.receptions?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([
              WidgetType.ACHATS,
              WidgetType.ACHATS_EVOLUTION,
            ]);
          }

          if (!stats.bilans?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([
              WidgetType.QUICK_WIN,
              WidgetType.CONSOS_PAR_ARTICLE,
            ]);
          }

          if (!stats.ofs?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([WidgetType.PRODUCTIONS]);
          }

          if (!stats.travail?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([WidgetType.HEURES_OFS]);
          }

          if (!stats.expeditions?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([
              WidgetType.VENTES,
              WidgetType.MARGE,
              WidgetType.CA_PERIODE_ARTICLE,
              WidgetType.CA_CUMULE_ARTICLE,
            ]);
          }

          if (!stats.mouvements?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([
              WidgetType.MOUVEMENTS_ARTICLES,
            ]);
          }

          if (!stats.lots?.length) {
            widgetToSqueeze = widgetToSqueeze.concat([
              WidgetType.STOCKS_ARTICLE,
            ]);
          }

          this.dashboardFacade
            .getWritableState()
            .squeezeWidgets.set(widgetToSqueeze);
        });
      },
      { allowSignalWrites: true },
    );
  }

  export(kind: "xlsx" | "pdf") {
    // TODO: brancher sur le service d'export
  }

  onSelectArticleId(articleId: string) {
    this.articleIdInput.set(articleId);
    this.articleId.set(articleId);
    this.startSearch.set(false);
  }

  ddmmToDate(ddmm: string, year: number) {
    const [dd, mm] = ddmm.split("/").map(parseNumber);
    return new Date(year, mm - 1, dd); // mois 0-based
  }

  protected readonly TypeDashboard = TypeDashboard;
}
